/**
 * EchoPaste WebCrypto Primitives
 *
 * Implements AES-GCM encryption/decryption, key generation, and SHA-256 hashing.
 * Uses Web Crypto API exclusively with random 96-bit IVs and base64url serialization.
 *
 * NOTE: These are cryptographic building blocks (primitives), NOT a complete E2EE system.
 */

import {
  isRecoveryKitEnvelope,
  isRecoveryKitPayload,
  type RecoveryKitEnvelope,
  type RecoveryKitPayload,
} from "@echopaste/protocol";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * Encodes Uint8Array into URL-safe Base64 string without padding.
 */
export function uint8ArrayToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Decodes URL-safe Base64 string into Uint8Array.
 */
export function base64UrlToUint8Array(base64url: string): Uint8Array {
  let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4 !== 0) {
    base64 += "=";
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Generates an AES-GCM 256-bit CryptoKey.
 */
export async function generateKey(): Promise<CryptoKey> {
  return await crypto.subtle.generateKey(
    {
      name: "AES-GCM",
      length: 256,
    },
    true,
    ["encrypt", "decrypt"]
  );
}

/**
 * Exports raw key bytes as base64url string.
 */
export async function exportKey(key: CryptoKey): Promise<string> {
  const raw = await crypto.subtle.exportKey("raw", key);
  return uint8ArrayToBase64Url(new Uint8Array(raw));
}

export interface ImportKeyOptions {
  extractable?: boolean;
}

/**
 * Imports raw key from base64url string into an AES-GCM CryptoKey.
 * Defaults to extractable=true for backward compatibility.
 */
export async function importKey(
  rawBase64Url: string,
  options?: ImportKeyOptions
): Promise<CryptoKey> {
  const bytes = base64UrlToUint8Array(rawBase64Url);
  const extractable = options?.extractable ?? true;
  return await crypto.subtle.importKey(
    "raw",
    bytes as unknown as BufferSource,
    {
      name: "AES-GCM",
      length: 256,
    },
    extractable,
    ["encrypt", "decrypt"]
  );
}

export interface EncryptedPayload {
  ciphertext: string;
  nonce: string;
}

/**
 * Encrypts plaintext string using AES-GCM with a fresh random 96-bit (12-byte) IV.
 * Returns base64url encoded ciphertext and nonce.
 */
export async function encryptText(
  plaintext: string,
  key: CryptoKey
): Promise<EncryptedPayload> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encodedPlaintext = encoder.encode(plaintext);

  const cipherBuffer = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv as unknown as BufferSource,
    },
    key,
    encodedPlaintext as unknown as BufferSource
  );

  return {
    ciphertext: uint8ArrayToBase64Url(new Uint8Array(cipherBuffer)),
    nonce: uint8ArrayToBase64Url(iv),
  };
}

/**
 * Decrypts AES-GCM ciphertext using the given base64url nonce and CryptoKey.
 * Throws an error on decryption failure (e.g. wrong key, tampered data).
 * NEVER falls back to returning raw ciphertext or suppressing decryption errors.
 */
export async function decryptText(
  ciphertext: string,
  nonce: string,
  key: CryptoKey
): Promise<string> {
  const iv = base64UrlToUint8Array(nonce);
  const cipherBytes = base64UrlToUint8Array(ciphertext);

  const decryptedBuffer = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: iv as unknown as BufferSource,
    },
    key,
    cipherBytes as unknown as BufferSource
  );

  return decoder.decode(decryptedBuffer);
}

/**
 * Computes SHA-256 digest of plaintext text returned as base64url string.
 * Used for client-side local fingerprinting and duplicate detection.
 * NOTE: Raw SHA-256 is NOT a safe server-visible fingerprint for short secrets.
 */
export async function hashText(text: string): Promise<string> {
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data as unknown as BufferSource);
  return uint8ArrayToBase64Url(new Uint8Array(hashBuffer));
}

export interface DeviceRequestInput {
  version: number;
  method: string;
  path: string;
  query: string;
  roomId: string;
  deviceId: string;
  timestamp: number;
  nonce: string;
  bodySha256: string;
}

const DEVICE_REQUEST_LABEL = "echopaste/device-request/v1";
const BASE64URL_256 = /^[A-Za-z0-9_-]{43}$/;

function isNonEmpty(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function rfc3986Encode(value: string): string {
  return encodeURIComponent(value).replace(/[!'()*]/g, (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`);
}

function canonicalizeQuery(query: string): string {
  const raw = query.startsWith("?") ? query.slice(1) : query;
  if (!raw) return "";
  const entries = raw.split("&").map((entry) => {
    const separator = entry.indexOf("=");
    const rawKey = separator < 0 ? entry : entry.slice(0, separator);
    const rawValue = separator < 0 ? "" : entry.slice(separator + 1);
    return [rfc3986Encode(decodeURIComponent(rawKey)), rfc3986Encode(decodeURIComponent(rawValue))] as const;
  });
  entries.sort(([leftKey, leftValue], [rightKey, rightValue]) => leftKey === rightKey ? (leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0) : (leftKey < rightKey ? -1 : 1));
  return entries.map(([key, value]) => `${key}=${value}`).join("&");
}

function assertValidDeviceRequest(input: DeviceRequestInput): void {
  if (input.version !== 1 || !isNonEmpty(input.method) || !isNonEmpty(input.path) || typeof input.query !== "string" ||
    !isNonEmpty(input.roomId) || !isNonEmpty(input.deviceId) || !Number.isSafeInteger(input.timestamp) || input.timestamp < 0 ||
    !BASE64URL_256.test(input.nonce) || !BASE64URL_256.test(input.bodySha256) || !/^\/[^?#\s]*$/.test(input.path)) {
    throw new Error("Invalid device request input");
  }
}

function canonicalizePath(path: string): string {
  const parsed = new URL(path, "https://echopaste.invalid");
  if (parsed.pathname !== path || parsed.search || parsed.hash) throw new Error("Invalid device request path");
  return parsed.pathname;
}

function lengthDelimited(value: string): string {
  return `${encoder.encode(value).byteLength}:${value}`;
}

/** Canonicalizes the fixed production-device request fields as UTF-8 byte-length-delimited values. */
export function canonicalDeviceRequest(input: DeviceRequestInput): string {
  assertValidDeviceRequest(input);
  const method = input.method.toUpperCase();
  if (!/^[A-Z]+$/.test(method)) throw new Error("Invalid device request method");
  const path = canonicalizePath(input.path);
  const query = canonicalizeQuery(input.query);
  return [DEVICE_REQUEST_LABEL, String(input.version), method, path, query, input.roomId, input.deviceId, String(input.timestamp), input.nonce, input.bodySha256]
    .map(lengthDelimited).join("");
}

/** SHA-256 of exact raw request bytes, encoded as unpadded base64url. */
export async function sha256Base64Url(rawBody: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", rawBody as unknown as BufferSource);
  return uint8ArrayToBase64Url(new Uint8Array(digest));
}

export async function signDeviceRequest(privateKey: CryptoKey, input: DeviceRequestInput): Promise<string> {
  const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, privateKey, encoder.encode(canonicalDeviceRequest(input)) as unknown as BufferSource);
  return uint8ArrayToBase64Url(new Uint8Array(signature));
}

/** Imports only public SPKI material and fails closed for every malformed proof or key. */
export async function verifyDeviceRequest(signingPublicKeySpki: string, input: DeviceRequestInput, signature: string): Promise<boolean> {
  try {
    if (!/^[A-Za-z0-9_-]{86}$/.test(signature)) return false;
    const publicKey = await crypto.subtle.importKey("spki", base64UrlToUint8Array(signingPublicKeySpki) as unknown as BufferSource, { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]);
    return await crypto.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, publicKey, base64UrlToUint8Array(signature) as unknown as BufferSource, encoder.encode(canonicalDeviceRequest(input)) as unknown as BufferSource);
  } catch {
    return false;
  }
}

export interface ProductionDeviceIdentity {
  deviceId: string;
  signingPrivateKey: CryptoKey;
  signingPublicKeySpki: string;
  signingFingerprint: string;
  agreementPrivateKey: CryptoKey;
  agreementPublicKeySpki: string;
  agreementFingerprint: string;
  createdAt: string;
}

/** SHA-256(SPKI bytes), encoded as unpadded base64url. */
export async function fingerprintPublicKeySpki(spki: string): Promise<string> {
  const bytes = base64UrlToUint8Array(spki);
  const digest = await crypto.subtle.digest("SHA-256", bytes as unknown as BufferSource);
  return uint8ArrayToBase64Url(new Uint8Array(digest));
}

async function exportSpki(publicKey: CryptoKey): Promise<string> {
  const spki = await crypto.subtle.exportKey("spki", publicKey);
  return uint8ArrayToBase64Url(new Uint8Array(spki));
}

/** Creates separate, non-extractable P-256 ECDSA and ECDH device identities. */
export async function generateProductionDeviceIdentity(): Promise<ProductionDeviceIdentity> {
  const signing = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"]) as CryptoKeyPair;
  const agreement = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"] ) as CryptoKeyPair;
  const signingPublicKeySpki = await exportSpki(signing.publicKey);
  const agreementPublicKeySpki = await exportSpki(agreement.publicKey);
  return {
    deviceId: crypto.randomUUID(),
    signingPrivateKey: signing.privateKey,
    signingPublicKeySpki,
    signingFingerprint: await fingerprintPublicKeySpki(signingPublicKeySpki),
    agreementPrivateKey: agreement.privateKey,
    agreementPublicKeySpki,
    agreementFingerprint: await fingerprintPublicKeySpki(agreementPublicKeySpki),
    createdAt: new Date().toISOString(),
  };
}

/** Generates the high-entropy, 256-bit secret embedded only in a displayed EP2 invitation. */
export function generateInvitationSecret(): string {
  return uint8ArrayToBase64Url(crypto.getRandomValues(new Uint8Array(32)));
}

/** Standard SHA-256 verifier for a random (not human-memorable) invitation secret. */
export async function invitationSecretVerifier(secret: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", base64UrlToUint8Array(secret) as unknown as BufferSource);
  return uint8ArrayToBase64Url(new Uint8Array(digest));
}

/** Explicit fields bound into Phase 4C's one-use ECDH/HKDF/AES-GCM operation. */
export interface ProvisioningContext {
  invitationId: string; requestId: string; roomId: string; keyVersion: number;
  senderDeviceId: string; recipientDeviceId: string;
  senderAgreementFingerprint: string; recipientAgreementFingerprint: string;
}
export interface ProvisioningCiphertext { ciphertext: string; nonce: string; }
export interface PairingSasFields {
  invitationId: string; requestId: string; inviterSigningFingerprint: string; inviterAgreementFingerprint: string;
  joinerSigningFingerprint: string; joinerAgreementFingerprint: string; invitationMaterial: string;
}
export interface CompletionChallenge { requestId: string; invitationId: string; provisioningId: string; deviceId: string; nonce: string; expiresAt: string; version: number; }

function canonical(label: string, fields: object): string {
  const values = fields as Record<string, string | number>;
  return `${label}|${Object.keys(values).sort().map((key) => `${key}:${String(values[key]).length}:${String(values[key])}`).join("|")}`;
}

export function canonicalProvisioningContext(context: ProvisioningContext): string {
  return canonical("echopaste/phase4c/provisioning/v1", context);
}

export function canonicalCompletionChallenge(challenge: CompletionChallenge): string {
  return canonical("echopaste/phase4c/completion/v1", challenge);
}

export async function importAgreementPublicKeySpki(spki: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("spki", base64UrlToUint8Array(spki) as unknown as BufferSource, { name: "ECDH", namedCurve: "P-256" }, false, []);
}

/** Derives a transient AES-GCM wrapping key; neither the ECDH output nor this key is persisted. */
export async function deriveProvisioningKey(privateKey: CryptoKey, peerPublicKey: CryptoKey, salt: string, context: ProvisioningContext): Promise<CryptoKey> {
  const shared = await crypto.subtle.deriveBits({ name: "ECDH", public: peerPublicKey }, privateKey, 256);
  const hkdf = await crypto.subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "HKDF", hash: "SHA-256", salt: base64UrlToUint8Array(salt) as unknown as BufferSource, info: encoder.encode(canonicalProvisioningContext(context)) as unknown as BufferSource }, hkdf, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

export async function encryptProvisioningPayload(payload: string, wrappingKey: CryptoKey, aad: string): Promise<ProvisioningCiphertext> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv as unknown as BufferSource, additionalData: encoder.encode(aad) as unknown as BufferSource }, wrappingKey, encoder.encode(payload) as unknown as BufferSource);
  return { ciphertext: uint8ArrayToBase64Url(new Uint8Array(ciphertext)), nonce: uint8ArrayToBase64Url(iv) };
}

export async function decryptProvisioningPayload(envelope: ProvisioningCiphertext, wrappingKey: CryptoKey, aad: string): Promise<string> {
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: base64UrlToUint8Array(envelope.nonce) as unknown as BufferSource, additionalData: encoder.encode(aad) as unknown as BufferSource }, wrappingKey, base64UrlToUint8Array(envelope.ciphertext) as unknown as BufferSource);
  return decoder.decode(plaintext);
}

/** Imports raw room material only into a non-extractable AES-GCM CryptoKey. */
export async function importProvisionedRoomKey(rawBase64Url: string): Promise<CryptoKey> { return importKey(rawBase64Url, { extractable: false }); }

/** Browser/PWA best-effort local-at-rest wrapping; the key is deliberately non-extractable. */
export async function generateLocalRoomWrappingKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

export async function wrapLocalRoomKey(rawRoomKeyMaterial: string, localWrappingKey: CryptoKey): Promise<ProvisioningCiphertext> {
  return encryptProvisioningPayload(rawRoomKeyMaterial, localWrappingKey, "echopaste/phase4c/local-room-wrap/v1");
}

export async function unwrapLocalRoomKey(wrapped: ProvisioningCiphertext, localWrappingKey: CryptoKey): Promise<string> {
  return decryptProvisioningPayload(wrapped, localWrappingKey, "echopaste/phase4c/local-room-wrap/v1");
}

export async function derivePairingSas(fields: PairingSasFields): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", encoder.encode(canonical("echopaste/phase4c/sas/v1", fields)) as unknown as BufferSource);
  const view = new DataView(digest); const n = (view.getUint32(0) % 100_000_000).toString().padStart(8, "0");
  return `${n.slice(0, 4)}-${n.slice(4)}`;
}

export async function signPairingCompletion(privateKey: CryptoKey, challenge: CompletionChallenge): Promise<string> {
  const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, privateKey, encoder.encode(canonicalCompletionChallenge(challenge)) as unknown as BufferSource);
  return uint8ArrayToBase64Url(new Uint8Array(signature));
}

export async function verifyPairingCompletion(signingPublicKeySpki: string, challenge: CompletionChallenge, signature: string): Promise<boolean> {
  try { const key = await crypto.subtle.importKey("spki", base64UrlToUint8Array(signingPublicKeySpki) as unknown as BufferSource, { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]); return crypto.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, key, base64UrlToUint8Array(signature) as unknown as BufferSource, encoder.encode(canonicalCompletionChallenge(challenge)) as unknown as BufferSource); } catch { return false; }
}

export const ROOM_KEY_ROTATION_DOMAIN = "echopaste/phase4/room-key-rotation/v1" as const;

export interface RoomKeyRotationContext {
  version: 1;
  roomId: string;
  keyVersion: number;
  senderDeviceId: string;
  recipientDeviceId: string;
  senderAgreementFingerprint: string;
  recipientAgreementFingerprint: string;
}

export function canonicalRoomKeyRotationContext(ctx: RoomKeyRotationContext): string {
  return canonical(ROOM_KEY_ROTATION_DOMAIN, {
    keyVersion: ctx.keyVersion,
    recipientAgreementFingerprint: ctx.recipientAgreementFingerprint,
    recipientDeviceId: ctx.recipientDeviceId,
    roomId: ctx.roomId,
    senderAgreementFingerprint: ctx.senderAgreementFingerprint,
    senderDeviceId: ctx.senderDeviceId,
    version: ctx.version,
  });
}

export async function deriveRoomKeyRotationKey(params: {
  privateKey: CryptoKey;
  peerPublicKeySpki: string;
  context: RoomKeyRotationContext;
  salt: Uint8Array;
}): Promise<CryptoKey> {
  const peerPublicKey = await importAgreementPublicKeySpki(params.peerPublicKeySpki);
  const shared = await crypto.subtle.deriveBits(
    { name: "ECDH", public: peerPublicKey },
    params.privateKey,
    256
  );
  const hkdf = await crypto.subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  const info = encoder.encode(canonicalRoomKeyRotationContext(params.context));
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: params.salt as unknown as BufferSource,
      info: info as unknown as BufferSource,
    },
    hkdf,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

const RECOVERY_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const RECOVERY_CODE_PREFIX = "EPRC";
export const RECOVERY_KIT_WRAP_DOMAIN = "echopaste/phase4/recovery-kit-wrap/v1" as const;
export const RECOVERY_KIT_AAD_DOMAIN = "echopaste/phase4/recovery-kit-aad/v1" as const;

export type RecoveryKitPayloadInput = RecoveryKitPayload;
export type RecoveryKitEnvelopeInput = RecoveryKitEnvelope;

function recoveryError(): Error { return new Error("Invalid recovery kit"); }
function validRecoveryText(value: unknown): value is string { return typeof value === "string" && value.trim().length > 0; }
function validRecoveryTimestamp(value: unknown): value is string { return validRecoveryText(value) && !Number.isNaN(Date.parse(value)); }
function validPositiveVersion(value: unknown): value is number { return typeof value === "number" && Number.isSafeInteger(value) && value > 0; }
function hasExactBytes(value: string, length: number): boolean {
  try { return base64UrlToUint8Array(value).byteLength === length; } catch { return false; }
}
function validatePayloadForCrypto(payload: unknown): asserts payload is RecoveryKitPayloadInput {
  if (!isRecoveryKitPayload(payload) || payload.keys.some((key) => !hasExactBytes(key.rawKeyMaterial, 32))) throw recoveryError();
}
function canonicalRecoveryData(secret: Uint8Array): string {
  let bits = 0; let bitCount = 0; let encoded = "";
  for (const byte of secret) { bits = (bits << 8) | byte; bitCount += 8; while (bitCount >= 5) { bitCount -= 5; encoded += RECOVERY_CODE_ALPHABET[(bits >>> bitCount) & 31]; } }
  if (bitCount !== 1) throw new Error("Invalid recovery secret");
  encoded += RECOVERY_CODE_ALPHABET[(bits & 1) << 4];
  return encoded;
}
async function recoveryChecksum(secret: Uint8Array): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", secret as unknown as BufferSource));
  const value = (digest[0] << 12) | (digest[1] << 4) | (digest[2] >>> 4);
  return [15, 10, 5, 0].map((shift) => RECOVERY_CODE_ALPHABET[(value >>> shift) & 31]).join("");
}
function formatRecoveryCode(payload: string): string { return `${RECOVERY_CODE_PREFIX}-${payload.match(/.{1,4}/g)!.join("-")}`; }

/** Generates a CSPRNG 256-bit recovery secret, formatted as 52 data plus 4 checksum characters. */
export async function generateRecoveryCode(): Promise<string> {
  const secret = crypto.getRandomValues(new Uint8Array(32));
  return formatRecoveryCode(`${canonicalRecoveryData(secret)}${await recoveryChecksum(secret)}`);
}

/** Parses only canonical 52-character Crockford payloads (the final four padding bits must be zero). */
export async function parseAndVerifyRecoveryCode(text: string): Promise<Uint8Array> {
  const compact = text.trim().replace(/[\s-]/g, "").toUpperCase().replace(/O/g, "0").replace(/[IL]/g, "1");
  const body = compact.length === 60 && compact.startsWith(RECOVERY_CODE_PREFIX) ? compact.slice(RECOVERY_CODE_PREFIX.length) : compact;
  if (body.length !== 56 || !/^[0-9ABCDEFGHJKMNPQRSTVWXYZ]{56}$/.test(body)) throw new Error("Invalid recovery code");
  const data = body.slice(0, 52); const checksum = body.slice(52);
  const finalValue = RECOVERY_CODE_ALPHABET.indexOf(data[51]);
  if ((finalValue & 15) !== 0) throw new Error("Invalid recovery code");
  let bits = 0; let bitCount = 0; const output: number[] = [];
  for (const character of data) { bits = (bits << 5) | RECOVERY_CODE_ALPHABET.indexOf(character); bitCount += 5; while (bitCount >= 8) { bitCount -= 8; output.push((bits >>> bitCount) & 255); } }
  if (output.length !== 32 || bitCount !== 4 || (bits & 15) !== 0) throw new Error("Invalid recovery code");
  const secret = new Uint8Array(output);
  if ((await recoveryChecksum(secret)) !== checksum) throw new Error("Invalid recovery code");
  return secret;
}

/** HKDF-SHA-256 derives the non-extractable AES-GCM kit wrapping key from 32-byte code and salt. */
export async function deriveRecoveryWrappingKey(secretBytes: Uint8Array, saltBytes: Uint8Array): Promise<CryptoKey> {
  if (secretBytes.byteLength !== 32 || saltBytes.byteLength !== 32) throw recoveryError();
  const source = await crypto.subtle.importKey("raw", secretBytes as unknown as BufferSource, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "HKDF", hash: "SHA-256", salt: saltBytes as unknown as BufferSource, info: encoder.encode(RECOVERY_KIT_WRAP_DOMAIN) as unknown as BufferSource }, source, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

/** Deterministic UTF-8 byte-length-delimited AAD for every authenticated recovery-kit field. */
export function canonicalRecoveryKitAad(envelope: Pick<RecoveryKitEnvelopeInput, "formatVersion" | "kitId" | "roomId" | "minKeyVersion" | "maxKeyVersion" | "exportedAt">): string {
  if (envelope.formatVersion !== 1 || !validRecoveryText(envelope.kitId) || !validRecoveryText(envelope.roomId) || !validPositiveVersion(envelope.minKeyVersion) || !validPositiveVersion(envelope.maxKeyVersion) || envelope.minKeyVersion > envelope.maxKeyVersion || !validRecoveryTimestamp(envelope.exportedAt)) throw recoveryError();
  return [RECOVERY_KIT_AAD_DOMAIN, String(envelope.formatVersion), envelope.kitId, envelope.roomId, String(envelope.minKeyVersion), String(envelope.maxKeyVersion), envelope.exportedAt].map(lengthDelimited).join("");
}

export interface MediaCryptoContext { projectId: string; roomId: string; keyVersion: number; }
export interface EncryptedBytes { ciphertext: string; nonce: string; }

function mediaAad(label: string, fields: Array<string | number>): Uint8Array {
  return encoder.encode([`echopaste/media/${label}/v1`, ...fields.map(String)].map(lengthDelimited).join(""));
}

async function encryptBytes(plaintext: Uint8Array, key: CryptoKey, aad: Uint8Array): Promise<EncryptedBytes> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce as BufferSource, additionalData: aad as BufferSource }, key, plaintext as BufferSource);
  return { ciphertext: uint8ArrayToBase64Url(new Uint8Array(ciphertext)), nonce: uint8ArrayToBase64Url(nonce) };
}

async function decryptBytes(envelope: EncryptedBytes, key: CryptoKey, aad: Uint8Array): Promise<Uint8Array> {
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: base64UrlToUint8Array(envelope.nonce) as BufferSource, additionalData: aad as BufferSource }, key, base64UrlToUint8Array(envelope.ciphertext) as BufferSource);
  return new Uint8Array(plaintext);
}

export async function wrapMediaContentKey(contentKey: CryptoKey, roomKey: CryptoKey, context: MediaCryptoContext): Promise<EncryptedBytes> {
  const raw = new Uint8Array(await crypto.subtle.exportKey("raw", contentKey));
  return encryptBytes(raw, roomKey, mediaAad("content-key", [context.projectId, context.roomId, context.keyVersion]));
}

export async function unwrapMediaContentKey(envelope: EncryptedBytes, roomKey: CryptoKey, context: MediaCryptoContext): Promise<CryptoKey> {
  const raw = await decryptBytes(envelope, roomKey, mediaAad("content-key", [context.projectId, context.roomId, context.keyVersion]));
  return crypto.subtle.importKey("raw", raw as BufferSource, { name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
}

export async function encryptMediaManifest(manifest: unknown, contentKey: CryptoKey, projectId: string): Promise<EncryptedBytes> {
  return encryptBytes(encoder.encode(JSON.stringify(manifest)), contentKey, mediaAad("manifest", [projectId]));
}

export async function decryptMediaManifest<T>(envelope: EncryptedBytes, contentKey: CryptoKey, projectId: string): Promise<T> {
  return JSON.parse(decoder.decode(await decryptBytes(envelope, contentKey, mediaAad("manifest", [projectId])))) as T;
}

export async function encryptMediaChunk(plaintext: Uint8Array, contentKey: CryptoKey, projectId: string, index: number): Promise<EncryptedBytes> {
  if (!Number.isSafeInteger(index) || index < 0) throw new Error("Invalid media chunk index");
  return encryptBytes(plaintext, contentKey, mediaAad("chunk", [projectId, index]));
}

export async function decryptMediaChunk(envelope: EncryptedBytes, contentKey: CryptoKey, projectId: string, index: number): Promise<Uint8Array> {
  if (!Number.isSafeInteger(index) || index < 0) throw new Error("Invalid media chunk index");
  return decryptBytes(envelope, contentKey, mediaAad("chunk", [projectId, index]));
}

function assertRecoveryWrappingKey(key: CryptoKey, usage: KeyUsage): void {
  const algorithm = key.algorithm as AesKeyAlgorithm;
  if (key.type !== "secret" || algorithm.name !== "AES-GCM" || algorithm.length !== 256 || !key.usages.includes(usage)) throw recoveryError();
}

function serializeRecoveryKitPayload(payload: RecoveryKitPayloadInput): string {
  return JSON.stringify({
    schemaVersion: payload.schemaVersion,
    roomId: payload.roomId,
    currentKeyVersion: payload.currentKeyVersion,
    keys: payload.keys.map(({ keyVersion, rawKeyMaterial }) => ({ keyVersion, rawKeyMaterial })),
    creatorDeviceId: payload.creatorDeviceId,
    exportedAt: payload.exportedAt,
  });
}

export async function encryptRecoveryKit(payload: RecoveryKitPayloadInput, wrappingKey: CryptoKey, options: { kitId: string; salt: Uint8Array }): Promise<RecoveryKitEnvelopeInput> {
  validatePayloadForCrypto(payload);
  assertRecoveryWrappingKey(wrappingKey, "encrypt");
  if (!validRecoveryText(options.kitId) || options.salt.byteLength !== 32) throw recoveryError();
  const versions = payload.keys.map((key) => key.keyVersion);
  const envelopeBase = { formatVersion: 1 as const, kitId: options.kitId, roomId: payload.roomId, minKeyVersion: Math.min(...versions), maxKeyVersion: Math.max(...versions), salt: uint8ArrayToBase64Url(options.salt), exportedAt: payload.exportedAt };
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const serialized = serializeRecoveryKitPayload(payload);
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce as unknown as BufferSource, additionalData: encoder.encode(canonicalRecoveryKitAad(envelopeBase)) as unknown as BufferSource }, wrappingKey, encoder.encode(serialized) as unknown as BufferSource);
  return { ...envelopeBase, nonce: uint8ArrayToBase64Url(nonce), ciphertext: uint8ArrayToBase64Url(new Uint8Array(ciphertext)) };
}

export async function decryptRecoveryKit(envelope: RecoveryKitEnvelopeInput, wrappingKey: CryptoKey): Promise<RecoveryKitPayloadInput> {
  if (!isRecoveryKitEnvelope(envelope) || !hasExactBytes(envelope.salt, 32) || !hasExactBytes(envelope.nonce, 12)) throw recoveryError();
  assertRecoveryWrappingKey(wrappingKey, "decrypt");
  try {
    const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: base64UrlToUint8Array(envelope.nonce) as unknown as BufferSource, additionalData: encoder.encode(canonicalRecoveryKitAad(envelope)) as unknown as BufferSource }, wrappingKey, base64UrlToUint8Array(envelope.ciphertext) as unknown as BufferSource);
    const payload: unknown = JSON.parse(decoder.decode(plaintext));
    validatePayloadForCrypto(payload);
    const versions = payload.keys.map((key) => key.keyVersion);
    if (payload.roomId !== envelope.roomId || payload.exportedAt !== envelope.exportedAt || Math.min(...versions) !== envelope.minKeyVersion || Math.max(...versions) !== envelope.maxKeyVersion) throw recoveryError();
    return payload;
  } catch { throw recoveryError(); }
}

